<template>
  <div class="patient-chat-page">
    <!-- Header -->
    <header class="chat-header">
      <div class="container">
        <div class="header-content">
          <div class="header-info">
            <h1>🏥 Health Information Assistant</h1>
            <p>Get general health information in your preferred language</p>
          </div>
          <div class="credits-badge">
            <span class="credits-icon">💳</span>
            <span>{{ credits }} questions left</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="chat-main">
      <div class="container">
        <!-- Disclaimer Banner -->
        <div class="disclaimer-banner">
          <span class="disclaimer-icon">⚠️</span>
          <p>
            <strong>Important:</strong> This provides general health information only, not medical advice. 
            Always consult a qualified healthcare provider for personal health concerns.
          </p>
        </div>

        <!-- Language Selector -->
        <div class="language-selector">
          <label>Ask your question in:</label>
          <select v-model="selectedLanguage">
            <option value="english">English</option>
            <option value="hindi">हिन्दी (Hindi)</option>
            <option value="telugu">తెలుగు (Telugu)</option>
            <option value="tamil">தமிழ் (Tamil)</option>
            <option value="kannada">ಕನ್ನಡ (Kannada)</option>
            <option value="malayalam">മലയാളം (Malayalam)</option>
            <option value="marathi">मराठी (Marathi)</option>
            <option value="bengali">বাংলা (Bengali)</option>
            <option value="gujarati">ગુજરાતી (Gujarati)</option>
            <option value="punjabi">ਪੰਜਾਬੀ (Punjabi)</option>
            <option value="odia">ଓଡ଼ିଆ (Odia)</option>
            <option value="urdu">اردو (Urdu)</option>
          </select>
        </div>

        <!-- Question Input -->
        <div class="question-section">
          <form @submit.prevent="submitQuestion" class="question-form">
            <textarea
              v-model="question"
              :placeholder="getPlaceholder()"
              rows="4"
              :disabled="isLoading || credits <= 0"
            ></textarea>
            <button type="submit" :disabled="!question.trim() || isLoading || credits <= 0">
              {{ isLoading ? 'Getting Answer...' : 'Ask Question' }}
            </button>
          </form>
          
          <div v-if="credits <= 0" class="no-credits-warning">
            <p>You're out of questions. <router-link to="/credits">Purchase more credits</router-link> to continue.</p>
          </div>
        </div>

        <!-- Answer Display -->
        <div v-if="answer" class="answer-section">
          <div class="answer-card">
            <div class="answer-header">
              <span class="answer-icon">🤖</span>
              <span>Health Information</span>
            </div>
            <div class="answer-content" v-html="formatAnswer(answer)"></div>
            <div class="answer-disclaimer">
              {{ disclaimer }}
            </div>
          </div>
          
          <button @click="askAnother" class="ask-another-btn">
            Ask Another Question
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-section">
          <div class="loading-card">
            <div class="loading-spinner"></div>
            <p>Getting health information...</p>
          </div>
        </div>

        <!-- Suggested Topics -->
        <div v-if="!answer && !isLoading" class="suggested-topics">
          <h3>Common Topics</h3>
          <div class="topics-grid">
            <button 
              v-for="topic in suggestedTopics" 
              :key="topic"
              @click="useSuggestedTopic(topic)"
              class="topic-btn"
            >
              {{ topic }}
            </button>
          </div>
        </div>

        <!-- Privacy Notice -->
        <div class="privacy-notice">
          <span class="privacy-icon">🔒</span>
          <p>Your questions are not stored. We only keep an anonymous count for service improvement.</p>
        </div>
      </div>
    </main>

    <!-- Emergency Banner -->
    <div class="emergency-banner">
      <p>
        <strong>🚨 Medical Emergency?</strong> 
        Call <a href="tel:108">108</a> or <a href="tel:112">112</a> immediately, or go to the nearest hospital.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { authToken, isAuthenticated } from '@/services/auth'
import { getCreditsInfo, askPatientQuestion } from '@/services/chat'

const router = useRouter()

// State
const question = ref('')
const answer = ref('')
const disclaimer = ref('')
const selectedLanguage = ref('english')
const isLoading = ref(false)
const credits = ref(0)

const suggestedTopics = [
  'What are symptoms of diabetes?',
  'How to manage high blood pressure?',
  'What causes headaches?',
  'Tips for better sleep',
  'When to see a doctor for fever?',
  'How to stay hydrated?',
  'What is a healthy diet?',
  'Signs of vitamin deficiency'
]

onMounted(async () => {
  await loadCredits()
})

async function loadCredits() {
  try {
    const data = await getCreditsInfo(authToken.value)
    credits.value = data.credits
  } catch (error) {
    console.error('Failed to load credits:', error)
  }
}

async function submitQuestion() {
  if (!question.value.trim() || isLoading.value || credits.value <= 0) return
  
  try {
    isLoading.value = true
    answer.value = ''
    
    const response = await askPatientQuestion(
      authToken.value, 
      question.value.trim(), 
      selectedLanguage.value
    )
    
    answer.value = response.answer
    disclaimer.value = response.disclaimer
    credits.value = response.credits_remaining
    
  } catch (error) {
    console.error('Failed to get answer:', error)
    alert('Failed to get answer: ' + error.message)
  } finally {
    isLoading.value = false
  }
}

function askAnother() {
  question.value = ''
  answer.value = ''
  disclaimer.value = ''
}

function useSuggestedTopic(topic) {
  question.value = topic
}

function getPlaceholder() {
  const placeholders = {
    english: 'Type your health question here...',
    hindi: 'अपना स्वास्थ्य प्रश्न यहाँ लिखें...',
    telugu: 'మీ ఆరోగ్య ప్రశ్నను ఇక్కడ టైప్ చేయండి...',
    tamil: 'உங்கள் உடல்நலக் கேள்வியை இங்கே தட்டச்சு செய்யவும்...',
    kannada: 'ನಿಮ್ಮ ಆರೋಗ್ಯ ಪ್ರಶ್ನೆಯನ್ನು ಇಲ್ಲಿ ಟೈಪ್ ಮಾಡಿ...',
    malayalam: 'നിങ്ങളുടെ ആരോഗ്യ ചോദ്യം ഇവിടെ ടൈപ്പ് ചെയ്യുക...',
    marathi: 'तुमचा आरोग्य प्रश्न येथे टाइप करा...',
    bengali: 'আপনার স্বাস্থ্য প্রশ্ন এখানে টাইপ করুন...',
    gujarati: 'તમારો આરોગ્ય પ્રશ્ન અહીં ટાઇપ કરો...',
    punjabi: 'ਆਪਣਾ ਸਿਹਤ ਸਵਾਲ ਇੱਥੇ ਟਾਈਪ ਕਰੋ...',
    odia: 'ଆପଣଙ୍କ ସ୍ୱାସ୍ଥ୍ୟ ପ୍ରଶ୍ନ ଏଠାରେ ଟାଇପ୍ କରନ୍ତୁ...',
    urdu: 'اپنا صحت کا سوال یہاں ٹائپ کریں...'
  }
  return placeholders[selectedLanguage.value] || placeholders.english
}

function formatAnswer(content) {
  return content
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
}
</script>

<style scoped>
.patient-chat-page {
  min-height: calc(100vh - 150px);
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.chat-header {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  padding: 24px 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.header-info h1 {
  font-size: 1.5rem;
  margin-bottom: 4px;
}

.header-info p {
  opacity: 0.9;
  font-size: 0.95rem;
}

.credits-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.2);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 500;
}

.credits-icon {
  font-size: 1.2rem;
}

/* Main Content */
.chat-main {
  flex: 1;
  padding: 24px 0;
}

/* Disclaimer Banner */
.disclaimer-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fef3c7;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.disclaimer-icon {
  font-size: 1.3rem;
  flex-shrink: 0;
}

.disclaimer-banner p {
  font-size: 0.9rem;
  color: #92400e;
  line-height: 1.5;
}

/* Language Selector */
.language-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.language-selector label {
  font-weight: 500;
  color: #475569;
}

.language-selector select {
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  outline: none;
}

.language-selector select:focus {
  border-color: #059669;
}

/* Question Section */
.question-section {
  margin-bottom: 24px;
}

.question-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.question-form textarea {
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
}

.question-form textarea:focus {
  border-color: #059669;
}

.question-form button {
  align-self: flex-end;
  padding: 14px 32px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.question-form button:hover:not(:disabled) {
  background: #047857;
}

.question-form button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.no-credits-warning {
  margin-top: 16px;
  padding: 16px;
  background: #fef2f2;
  border-radius: 8px;
  text-align: center;
  color: #dc2626;
}

.no-credits-warning a {
  color: #059669;
  font-weight: 500;
}

/* Answer Section */
.answer-section {
  margin-bottom: 24px;
}

.answer-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  overflow: hidden;
}

.answer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
}

.answer-icon {
  font-size: 1.3rem;
}

.answer-content {
  padding: 20px;
  line-height: 1.7;
  color: #334155;
  font-size: 1rem;
}

.answer-disclaimer {
  padding: 16px 20px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 0.9rem;
  border-top: 1px solid #d1fae5;
}

.ask-another-btn {
  margin-top: 16px;
  width: 100%;
  padding: 14px;
  background: white;
  color: #059669;
  border: 2px solid #059669;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.ask-another-btn:hover {
  background: #059669;
  color: white;
}

/* Loading State */
.loading-section {
  margin-bottom: 24px;
}

.loading-card {
  background: white;
  padding: 40px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-card p {
  color: #64748b;
}

/* Suggested Topics */
.suggested-topics {
  margin-bottom: 24px;
}

.suggested-topics h3 {
  font-size: 1rem;
  color: #475569;
  margin-bottom: 12px;
}

.topics-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.topic-btn {
  padding: 10px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.topic-btn:hover {
  background: #059669;
  color: white;
  border-color: #059669;
}

/* Privacy Notice */
.privacy-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  background: #f1f5f9;
  border-radius: 10px;
  margin-bottom: 24px;
}

.privacy-icon {
  font-size: 1.2rem;
}

.privacy-notice p {
  font-size: 0.85rem;
  color: #64748b;
}

/* Emergency Banner */
.emergency-banner {
  background: #dc2626;
  color: white;
  padding: 12px 20px;
  text-align: center;
}

.emergency-banner p {
  font-size: 0.9rem;
}

.emergency-banner a {
  color: white;
  font-weight: 700;
  text-decoration: underline;
}

/* Mobile Responsive */
@media (max-width: 640px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-info h1 {
    font-size: 1.3rem;
  }
  
  .language-selector {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .language-selector select {
    width: 100%;
  }
  
  .question-form button {
    width: 100%;
  }
}
</style>